import React from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Languages, Check } from 'lucide-react';
import { UILanguage } from '@/i18n/types';

interface UILanguageSelectorProps {
  variant?: 'select' | 'dropdown' | 'button';
  size?: 'sm' | 'md' | 'lg';
  showFlag?: boolean;
  showNativeName?: boolean;
  disabled?: boolean;
  className?: string;
}

export const UILanguageSelector: React.FC<UILanguageSelectorProps> = ({
  variant = 'select',
  size = 'md',
  showFlag = true,
  showNativeName = true,
  disabled = false,
  className = ''
}) => {
  const { currentLanguage, currentLanguageInfo, availableLanguages, changeLanguage, t } = useI18n();

  const formatLanguageDisplay = (langInfo: typeof currentLanguageInfo, compact = false) => {
    const parts = [];
    
    if (showFlag) {
      parts.push(langInfo.flag);
    }
    
    if (compact) {
      parts.push(langInfo.code.split('-')[0].toUpperCase());
    } else if (showNativeName) {
      parts.push(langInfo.nativeName);
    } else {
      parts.push(langInfo.name);
    }
    
    return parts.join(' ');
  };

  if (variant === 'select') {
    return (
      <div className={`space-y-2 ${className}`}>
        <Select
          value={currentLanguage}
          onValueChange={(value) => changeLanguage(value as UILanguage)}
          disabled={disabled}
        >
          <SelectTrigger className={`w-full ${size === 'sm' ? 'h-8' : size === 'lg' ? 'h-12' : 'h-10'}`}>
            <SelectValue>
              {formatLanguageDisplay(currentLanguageInfo)}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {availableLanguages.map((langInfo) => (
              <SelectItem key={langInfo.code} value={langInfo.code}>
                <div className="flex items-center gap-2">
                  <span>{formatLanguageDisplay(langInfo)}</span>
                  {langInfo.code === currentLanguage && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }


  // Map 'md' to 'default' for Button size prop
  const buttonSize = size === 'md' ? 'default' : size;

  if (variant === 'dropdown') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size={buttonSize}
            disabled={disabled}
            className={`gap-2 ${className}`}
          >
            <Languages className="h-4 w-4" />
            {formatLanguageDisplay(currentLanguageInfo, true)}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {availableLanguages.map((langInfo) => (
            <DropdownMenuItem
              key={langInfo.code}
              onClick={() => changeLanguage(langInfo.code)}
              className="flex items-center justify-between"
            >
              <span>{formatLanguageDisplay(langInfo)}</span>
              {langInfo.code === currentLanguage && (
                <Check className="h-4 w-4 text-primary" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Button variant - cycles through languages
  const handleCycleLanguage = () => {
    const currentIndex = availableLanguages.findIndex(lang => lang.code === currentLanguage);
    const nextIndex = (currentIndex + 1) % availableLanguages.length;
    changeLanguage(availableLanguages[nextIndex].code);
  };


  return (
    <Button
      variant="outline"
      size={buttonSize}
      onClick={handleCycleLanguage}
      disabled={disabled}
      className={`gap-2 ${className}`}
      title={t('settings.uiLanguage')}
    >
      <Languages className="h-4 w-4" />
      {formatLanguageDisplay(currentLanguageInfo, true)}
    </Button>
  );
};
